import {
    merge,
    filter,
    flow,
    identity,
    includes,
    join,
    partialRight,
    toLower,
    values,
    isObject,
    round,
    toString,
    find,
    has,
    parseInt,
    sortBy,
    lowerFirst
} from "lodash";
import { normalize } from 'normalizr';
import { camelizeKeys } from 'humps';
import moment from "moment";
import keycloak from "./Keycloak";

export const contextSearch = (array, substr) => {
    const flatObjects = (seq) => {
        return seq.map((item) => {
            if ( isObject(item) ) {
                return join(values(item));
            }

            return item
        });
    };

    return filter(array, flow(
        identity,
        values,
        flatObjects,
        join,
        toLower,
        partialRight(includes, toLower(substr))
    ));
};

export const createMarkup = html => {
    return { __html: html }
};

const escapeRegExp = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const highlightText = (text, context) => {
    text = toString(text);
    return(text
        ? context
            ? text.replace(new RegExp(escapeRegExp(context), "gi"), `<span class="sp-table-highlighted-text">$&</span>`)
            : text.replace(/(<([^>]+)>)/gi, "")
        : text
    )
};

export const replaceBrackets = (text) => typeof text == "string" ? text.replace(/\<(\w+)\>/gm, "&lt;$1&gt;") : text;

export const parseJSON = (response) => {
    if (response.status >= 500) {
        return new Promise(resolve => {
            return resolve({
                status: response.status,
                ok: false,
                json: {
                    code: response.status,
                    message: response.statusText,
                    success: false
                },
            });
        });
    };
    const apiServerVersion = response.headers.get("x-thermos-version");
    const appVersion = response.headers.get("x-app-version");
    if (appVersion) {
        window.localStorage.setItem("version", appVersion);
    }
    const contentType = response.headers.get("content-type");
    const isFile = typeof contentType === "string"
        ? contentType.indexOf("text") > -1 || contentType.indexOf("application/pdf") > -1
        : false;

    if (apiServerVersion && apiServerVersion !== process.env.REACT_APP_API_VERSION) {
        return new Promise(resolve => resolve({
                ok: false,
                json: {
                    code: 409,
                    message: `Version ${apiServerVersion} of the server is incompatible with the client version ${process.env.REACT_APP_API_VERSION}`},
            }));
    };
    if (response.status === 200 || response.status === 201) {
        return isFile
            ? new Promise(resolve => response.blob().then(blob => {
                let disposition = response.headers.get('Content-Disposition');
                let filename = disposition.split(/;(.+)/)[1].split(/=(.+)/)[1];

                return resolve({
                    status: response.status,
                    ok: response.ok,
                    json: {
                        url: URL.createObjectURL(blob),
                        saveAs: filename || "untitled"
                    }
                })
            }))
            : new Promise(resolve => response.json().then(json =>
                resolve({
                    status: response.status,
                    ok: response.ok,
                    json,
                })));
    }
    else if (response.status === 204) {
        return new Promise(resolve => response.text().then(text =>
            resolve({
                status: response.status,
                ok: response.ok,
                json: {},
            })));
    };

    return new Promise(resolve => response.text().then(text => {
        let reason = '';

        try {
            var json = JSON.parse(text);
            if (json && typeof json === 'object' && json !== null) {
                reason = 'reason' in json ? json.reason : ('message' in json ? json.message : 'Unknown error');
            }
        } catch (err) {
            reason = "Unknown error";
         };

        return resolve({
            status: response.status,
            ok: false,
            json: {
                code: response.status,
                message: reason,
                success: false
            },
        });
    }));
};

export const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
}

export const fetchData = async (endpoint, newoptions, schema={}) => {
    try {
        await keycloak.updateToken(10);
        const token = keycloak.token;
        let options = {
            credentials: 'include',
            headers: {
                Authorization: `Bearer ${token}`
            }
        };
        options = merge(options, newoptions);
        const response = await fetch(endpoint, options);
        const parsedResponse = await parseJSON(response);
        if (response.ok) {
            const camelizedJson = camelizeKeys(parsedResponse.json);
            return Object.assign({}, normalize(camelizedJson, schema));
        } else {
            throw parsedResponse.json;
        };
    } catch (error) {
        throw {
            code: error ? error.code : null,
            message: error ? error.message : error,
            success: false
        }
    }
};

export const isNone = (value) => {
    return toLower(value) === 'none';
};

export const noneToNull = (value) => {
    return toLower(value) === 'none' ? null : value;
};

export const formatDate = (date, format) => moment(date).isValid() ? moment.utc(date).format(format): date;

export const fixPackageSize = (bytes) => {
    if (bytes == 0 || typeof bytes != "number") return '0 byte';
    let sizes = ['bytes', 'KB', 'MB', 'GB', 'TB'];
    let i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
    if ((Math.ceil(bytes / Math.pow(1024, i))).toString().length > 3) i += 1;
    return round(bytes / Math.pow(1024, i), 2) + ' ' + sizes[i];
};

export const numberWithSpaces = (number) => {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
};

export const normilizeFilename = (filename) => {
    // remove non-ascii characters
    let str = filename.replace(/[^\x00-\x7F]/g, "");
    // replace white spaces with underscores and remove non-secure characters
    str = str.split(" ").join("_").replace(/[^A-Za-z0-9_.-]/g, "");
    // remove leading and trailing dots and underscores
    str = str.replace(/^[._]+|[._]+$/g, "");

    return str;
};

export const compareArrays = (a, b) => Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((el, idx) => {
    if (Array.isArray(el) && Array.isArray(b[idx])) {
        return compareArrays(el, b[idx])
    }
    return el === b[idx]
});

export const getContractStatus = (expiry) => {
    let days = moment(expiry).startOf("day").diff(moment().startOf("day"), "days");
    return days < 0 ? -1 : days > 10 ? 1 : 0
};

export const getCurrentAppVersion = () => {
    let currentVersion = `${process.env.REACT_APP_VERSION}-${process.env.REACT_APP_UPD_LEVEL}`;
    if (process.env.REACT_APP_BUILD > 0) {
        currentVersion += `.${process.env.REACT_APP_BUILD}`;
    };

    return currentVersion;
};

export const getTitle = (key, array) => {
    let item = find(array, d => has(d, "key") && d.key == key);
    return item && has(item, "title")
        ? item.title
        : key
};

const URL_OPTIONS = {
    filter: null,
    column: null,
    order: null,
    page: 1,
    context: "",
    grant: [],
    disabled: 0,
    grant_expired: 0,
    status: [],
    expired: 0,
    sysarch: [],
    minos: [],
    swtype: [],
    verified: 0,
    action: [],
    archived: 0,
    product: []
};

export const capitalizeFirstLetter = (str) => typeof str == "string"
    ? str.charAt(0).toUpperCase() + String(str).slice(1)
    : str;

export const snakeCase = str => typeof str == "string" && str.length
    ? str[0] == str[0].toLowerCase()
        ? lowerFirst(str).replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)
        : str.toLowerCase()
    : str;

export const camelCase = str => typeof str == "string"
    ? str.replace(/([_][a-z])/g, group =>
            group
                .toUpperCase()
                .replace('_', '')
        )
    : str;

const createPairs = (str, options={}) => {
    let obj = {};
    let [itemKey, itemValue] = str.split(/=(.*)/s);
    if (options.hasOwnProperty(itemKey)) {
        if (itemValue !== "" || itemValue !== undefined || itemValue !== null) {
            if (itemValue.indexOf('=') > -1) {
                obj[itemKey] = createPairs(itemValue);
            } else {
                if (!obj[itemKey] && Array.isArray(options[itemKey])) {
                    obj[itemKey] = [];
                }
                Array.isArray(options[itemKey])
                    ? obj[itemKey] = obj[itemKey].concat(itemValue.split(",").map(val => camelCase(val)))
                    : obj[itemKey] = typeof options[itemKey] === "number" ? parseInt(itemValue) : itemKey == "context" ? itemValue : camelCase(itemValue);}
            }
    }
    return obj
};

export const queryStringToObject = (queryString = "") => {
    let queryObject = {};
    queryString && decodeURIComponent(queryString.replace('?', '')).split('&').map((itemString) => {
        queryObject = Object.assign({}, queryObject, createPairs(itemString, URL_OPTIONS));
    });
    return queryObject;
};

const getPairs = (key, value) => {
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
        return encodeURIComponent(key) + '=' + Object.keys(value).map(k => getPairs(k, value[k])).join(';')
    };
    return `${encodeURIComponent(key)}=${Array.isArray(value)
        ? value.map(item => encodeURIComponent(snakeCase(item))).join(',')
        : encodeURIComponent(snakeCase(value))}`;
};

export const createQueryString = (queryObject = {}) => {
    let urlOptionsKeys = Object.keys(URL_OPTIONS);

    let queryString = sortBy(Object.keys(queryObject), key => urlOptionsKeys.indexOf(key))
        .filter((key) => queryObject[key] !== undefined && queryObject[key] !== null && queryObject[key] !== "" && urlOptionsKeys.includes(key) && !(Array.isArray(queryObject[key]) && !queryObject[key].length))
        .map((key) => getPairs(key, queryObject[key])
        ).join('&');

        return queryString ? `?${queryString}` : "";
};

export const removeQueryParam = (str, params=[]) => {
    if (typeof str == "string") {
        params.map(param => {
            let re = new RegExp(String.raw`&${param}(\=[^&]*)?(?=&|$)|${param}(\=[^&]*)?(&|$)`, "gi");
            str = str.replace(re, "");
        })
        return str;
    };
    return str;
}